import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import { useState } from "react";

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_cheers-book/artifacts/ajhyh6sc_Design%20sans%20titre-4.png";
export const BRAND = "Saveurs Normandes";

const links = [
  { to: "/evenements", label: "Événements" },
  { to: "/reservation-table", label: "Réserver" },
  { to: "/evenement-prive", label: "Bar mobile" },
  { to: "/carte", label: "Carte" },
  { to: "/boutique", label: "Boutique" },
  { to: "/cartes-cadeaux", label: "Cadeaux" },
  { to: "/mon-histoire", label: "Mon histoire" },
];

export default function Header() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Saveurs Normandes"
            className="h-12 w-12 rounded-full object-cover bg-black"
            data-testid="brand-logo"
          />
          <div className="leading-none hidden sm:block xl:hidden 2xl:block whitespace-nowrap">
            <div className="font-display text-xl tracking-tight">{BRAND}</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mt-1">
              Cocktails · Events · Normandie
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-6" data-testid="primary-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-[13px] 2xl:text-sm tracking-wide whitespace-nowrap transition-colors ${
                  isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2 2xl:gap-3">
          {loading ? (
            // Prevent auth flash: reserve the space with a neutral skeleton
            // while /auth/me resolves so we don't briefly render the
            // "Barman · Serveur / Connexion" buttons for an already-logged-in
            // user (or vice-versa) on hard-refresh.
            <div className="flex items-center gap-2" data-testid="header-auth-loading">
              <div className="h-8 w-24 rounded-full bg-muted/40 animate-pulse" />
              <div className="h-8 w-28 rounded-full bg-muted/40 animate-pulse" />
            </div>
          ) : user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav("/connexion?tab=staff")}
                data-testid="header-staff-btn"
                className="text-xs uppercase tracking-[0.15em] text-primary hover:text-primary hover:bg-primary/10"
                title="Accès réservé au personnel (barman/serveur)"
              >
                Barman · Serveur
              </Button>
              {(user.role === "admin" || user.role === "editor") && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => nav("/admin")}
                  data-testid="header-admin-btn"
                >
                  Admin
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav("/compte")}
                data-testid="header-account-btn"
              >
                {user.name?.split(" ")[0] || "Compte"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="header-logout-btn"
              >
                Déconnexion
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav("/connexion?tab=staff")}
                data-testid="header-staff-btn"
                className="text-xs uppercase tracking-[0.15em] text-primary hover:text-primary hover:bg-primary/10"
                title="Accès réservé au personnel (barman/serveur)"
              >
                Barman · Serveur
              </Button>
              <Button variant="ghost" size="sm" onClick={() => nav("/connexion")} data-testid="header-login-btn">
                Connexion
              </Button>
              <Button
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-5"
                onClick={() => nav("/inscription")}
                data-testid="header-register-btn"
              >
                Créer un compte
              </Button>
            </>
          )}
        </div>

        <button
          className="xl:hidden text-foreground"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-border bg-background/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-6 py-5 flex flex-col gap-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-base text-muted-foreground hover:text-foreground"
                data-testid={`mobile-nav-${l.to.replace("/", "")}`}
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-border flex flex-col gap-2">
              {loading ? (
                <div className="h-10 w-full rounded-md bg-muted/40 animate-pulse" />
              ) : user ? (
                <>
                  <Button variant="outline" onClick={() => { setOpen(false); nav("/connexion?tab=staff"); }} data-testid="mobile-staff-btn" className="text-primary border-primary/40">
                    Barman · Serveur
                  </Button>
                  <Button variant="outline" onClick={() => { setOpen(false); nav("/compte"); }} data-testid="mobile-account-btn">
                    Mon compte
                  </Button>
                  {(user.role === "admin" || user.role === "editor") && (
                    <Button variant="outline" onClick={() => { setOpen(false); nav("/admin"); }} data-testid="mobile-admin-btn">
                      Admin
                    </Button>
                  )}
                  <Button onClick={async () => { await logout(); setOpen(false); nav("/"); }} data-testid="mobile-logout-btn">
                    Déconnexion
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { setOpen(false); nav("/connexion?tab=staff"); }} data-testid="mobile-staff-btn" className="text-primary border-primary/40">
                    Barman · Serveur
                  </Button>
                  <Button variant="outline" onClick={() => { setOpen(false); nav("/connexion"); }} data-testid="mobile-login-btn">
                    Connexion
                  </Button>
                  <Button onClick={() => { setOpen(false); nav("/inscription"); }} data-testid="mobile-register-btn">
                    Créer un compte
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
